import React, { useEffect, useState, useRef } from 'react';
import { get, post, put } from 'helpers/api';
import axios from 'axios';

import LoadingSpinner from 'components/ui/loading_spinner';
import Pagination from 'components/ui/pagination';
import { showSnackbar } from 'components/ui/snackbar/helper';

import Modal from 'components/modal';
import PropTypes from 'prop-types';
import ReportTable from './table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUndo } from '@fortawesome/pro-solid-svg-icons';
import ReportFilters from './filters';
import AllFiltersModal from './all_filters_modal';
import SavedReportModal from './saved_report_modal';
import { CHILD_FILTER_TYPE, filterTypeOptions } from './util';
import { Button } from 'components/ui/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import humanizeString from 'humanize-string';
import { v4 as uuid } from 'uuid';
import { getCookieValue } from 'helpers/cookie_helpers';
import ColumnselectModal from './column_select';
import ReportHeaderActions, { GENERATING_CSV_STEPS } from './header_actions';
import isEqual from 'lodash/isEqual';
import snakecase from 'lodash/snakeCase';
import ReportBreadCrumbs from './breadcrumbs';

const pageSize = 25;

function setupSelectedFilters(filters, applyDefaultValue = true) {
  return Object.keys(filters).reduce((acc, filterKey) => {
    const filter = filters[filterKey];
    const filterType = filter.type;
    const filterStartingValue = {
      [filterKey]: applyDefaultValue && filter.defaultValue
        ? filterTypeOptions[filterType].setupFilters(filter.defaultValue)
        : filterTypeOptions[filterType].setupFilters()
    };
    if (filter.childFilter) {
      filterStartingValue[filter.childFilter.key] = filterTypeOptions[CHILD_FILTER_TYPE].setupFilters();
    }

    return Object.assign(acc, filterStartingValue);
  }, {});
}

// Drops selected values that no longer appear among a filter's derived options, e.g. an
// office/company selection that falls out of the Status-filtered dropdown when Status changes.
function pruneStaleSelections(selectedFilters, derivedFilterValues) {
  return Object.keys(derivedFilterValues).reduce((acc, key) => {
    const options = derivedFilterValues[key]?.options;
    const selected = acc[key];
    if (!options || !Array.isArray(selected)) return acc;

    const validValues = new Set(options.map(option => option.value));
    const prunedSelected = selected.filter(value => validValues.has(value));
    if (prunedSelected.length === selected.length) return acc;

    return { ...acc, [key]: prunedSelected };
  }, selectedFilters);
}

function setupSelectedColumns(savedReport, columns) {
  if (savedReport) {
    const selectedColumns = savedReport.selectedColumns;
    if (!selectedColumns?.length) return defaultColumns(columns);

    return selectedColumns.map((selectedColumnName) => {
      return columns.find(column => column.name === selectedColumnName);
    }).filter(Boolean); // filters out undefined values
  }
  return defaultColumns(columns);
}

function defaultColumns(columns) {
  return columns.filter(column => column.default);
}

function setupBaseReport(savedReport, columns, filters, name, reportClass) {
  const reportFilters = setupSelectedFilters(filters);
  const sortableColumn = columns.find(column => column.sortable);
  const reportSorts = {
    column: sortableColumn ? sortableColumn.key : '',
    direction: 'asc'
  };

  if (savedReport) {
    return {
      ...savedReport,
      filters: { ...reportFilters, ...savedReport.filters },
      sorts: savedReport.sorts || reportSorts
    };
  } else {
    return {
      filters: reportFilters,
      name: name,
      report_class: reportClass,
      shared: 'false',
      sorts: reportSorts
    };
  }
}

export const PAGE_PATHS = {
  DEFAULT: 'default',
  GENERATE_CSV: 'generate_csv',
  GENERATE_PDF: 'generate_pdf',
  GENERATING_PDF: 'generating_pdf',
};

export default function BaseReport({
  allFilterOrder,
  baseReportName,
  columns,
  countOnlyOnFilterChange = false,
  csvDownloadMessage,
  csvDownloadPath,
  csvIgnoresSelection = false,
  defaultGroupingOptions,
  deriveFilterValues,
  disablePdf = false,
  emptyResultsMessage,
  filters,
  filterValues,
  hideColumnCustomization = false,
  hideSavedReports = false,
  itemSingularLabel,
  renderColumnSelect,
  renderFilters,
  renderPageInfo,
  reportClass,
  reportPath,
  reportsPath,
  reportType,
  saveAsMessage,
  savedReport,
  savedReportsUrl,
  searchTextRansackLabel,
  timeZone,
  transformRecords,
  updatedSavedReportUrl,
  url,
  onSetSavedReport
}) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ currentPage: 1, pageSize });
  // The filters meta's count was fetched with. Until a fetch for new filters succeeds, the count is the old ones'.
  const [countedFilters, setCountedFilters] = useState(null);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);

  const [report, setReport] = useState(setupBaseReport(savedReport, columns, filters, baseReportName, reportClass));

  const [pagePath, setPagePath] = useState(PAGE_PATHS.DEFAULT);

  const [showAllFiltersModal, setShowAllFiltersModal] = useState(false);
  const [generatingCSVStep, setGeneratingCSVStep] = useState(GENERATING_CSV_STEPS.DEFAULT);
  const [showSaveModal, setShowModal] = useState(false);

  const [generatePdfOptions, setGeneratePdfOptions] = useState({ sort_by: '', group_by: '' });
  const [editColumnsModalOpen, setEditColumnsModalOpen] = useState(false);
  // An account without Edit Columns only ever sees the defaults, even on a view saved with more.
  const [selectedColumns, setSelectedColumns] = useState(
    setupSelectedColumns(hideColumnCustomization ? null : savedReport, columns)
  );
  const [allSelected, setAllSelected] = useState(false);
  const [primaryBrokerOnly, setPrimaryBrokerOnly] = useState(true);

  const generatingPdf = pagePath === PAGE_PATHS.GENERATING_PDF;
  const isCompReport = reportType === 'saleComp' || reportType === 'leaseComp';
  const fetchRecordsId = useRef();
  const downloadKey = useRef(uuid());
  const downloadIntervalId = useRef();
  const abortControllerRef = useRef();

  const loadingPdfText = 'Gathering all matching properties for your PDF, this could take a while...';

  useEffect(() => {
    fetchRecords();
  }, []);

  // Backfill the export descriptor for reports saved before email scheduling existed, so
  // list-page "Schedule email" works without an explicit re-save (it can't translate filters itself).
  // Only the view's creator can save it.
  useEffect(() => {
    if (!savedReport?.owned || savedReport.exportQuery) return;

    let exportQuery;
    try {
      exportQuery = buildExportQuery();
    } catch {
      return;
    }

    // Fire-and-forget: only persist export_query to the DB; the update response is a different
    // serializer shape than this prop, so don't swap it into local state.
    put(updatedSavedReportUrl, { exportQuery }).catch(() => undefined);
  }, []);

  const firstPrimaryBrokerRender = useRef(true);
  useEffect(() => {
    if (firstPrimaryBrokerRender.current) {
      firstPrimaryBrokerRender.current = false;
      return;
    }
    debouncedFetchRecords({ currentPage: 1 });
  }, [primaryBrokerOnly]);

  const togglePrimaryBrokerFilter = () => {
    setPrimaryBrokerOnly(!primaryBrokerOnly);
  };

  const behaviorToggle = () => {
    if (reportType !== 'property') return null;

    return {
      value: 'togglePrimaryBrokerFilter',
      label: 'Primary Broker Only',
      checked: primaryBrokerOnly,
      onChange: togglePrimaryBrokerFilter,
      optionToToggle: 'broker'
    };
  };

  const reportParams = async () => {
    const fieldsToSend = selectedColumns.map(column => snakecase(column.value));
    const recordIds = allSelected ? await fetchAllMatchingProperties() : selectedRecordIds;
    if (!recordIds) return null;

    // TODO: remove explicit checks of reportType
    if (reportType === 'property') {
      return {
        inventoryReport: {
          fields: fieldsToSend,
          groupBy: generatePdfOptions['group_by']['value'],
          propertyIds: recordIds,
          sortBy: generatePdfOptions['sort_by']['value']
        }
      };
    }

    return {
      researchCompReport: {
        compAttributes: fieldsToSend,
        comp_ids: recordIds,
        docsDocumentTemplateId: null,
        name: null
      },
      type: reportType === 'leaseComp' ? 'lease' : 'sale',
      newReport: true
    };
  };

  function generatePdfButtonText() {
    if (generatingPdf) {
      return 'Generating PDF...';
    } else if (pagePath === PAGE_PATHS.GENERATE_PDF) {
      return 'Download PDF';
    }

    return 'Save';
  }

  useEffect(() => {
    if (generatingPdf) {
      const generatePdf = async () => {
        try {
          const params = await reportParams();
          if (!params) {
            setPagePath(PAGE_PATHS.DEFAULT);
            return;
          }

          const data = await post(reportPath, params);
          window.location.assign(data.editPath);
        } catch (error) {
          alert('There was an error generating the PDF. Please try again.');
          setLoading(false);
          setPagePath(PAGE_PATHS.DEFAULT);
        }
      };

      generatePdf();
    }
  }, [selectedColumns]);

  function generatePdf(newColumns) {
    setSelectedColumns(newColumns);
    setPagePath(PAGE_PATHS.GENERATING_PDF);
  }

  function saveSelectedColumns(newColumns) {
    setSelectedColumns(newColumns);
    setEditColumnsModalOpen(false);
  }

  const isSelected = record => selectedRecordIds.includes(record.uniqueId || record.id);

  function ransackParams(updatedValues = {}) {
    const valuesToUse = {
      selectedFilterValues: report.filters,
      currentPage: meta.currentPage,
      sortColumn: report.sorts.column,
      sortOrder: report.sorts.direction,
      ...updatedValues
    };
    // A report with no sortable column has nothing to sort on, so it sends no sort.
    const sortColumn = columns.find(column => column.key === valuesToUse.sortColumn);
    const startingObject = sortColumn ? { ['q[s]']: `${sortColumn.ransackLabel} ${valuesToUse.sortOrder}` } : {};
    if (searchTextRansackLabel) {
      startingObject[`q[${searchTextRansackLabel}]`] = valuesToUse.selectedFilterValues.search ?? '';
    }
    if (timeZone) {
      startingObject.time_zone = timeZone;
    }
    if (reportType !== 'user') {
      startingObject['q[property_draft_eq]'] = false;
    }

    // The Primary Broker Only switch the shared bar offers, which only some report types get.
    const toggle = behaviorToggle();

    return Object.keys(filters).reduce((acc, filterKey) => {
      const filterData = filters[filterKey];
      const valuesForKey = valuesToUse.selectedFilterValues[filterKey];
      const filterType = filterData.type;
      const paramsForFilter = filterTypeOptions[filterType].ransackParams(filters, filterKey, valuesForKey);
      if (toggle?.checked && toggle.optionToToggle === filterKey && valuesForKey.length > 0) {
        Object.assign(acc, { 'q[property_projects_users_primary_eq]': true });
      }

      if (filterData.childFilter) {
        const childFilterValue = valuesToUse.selectedFilterValues[filterData.childFilter.key];
        const childRansack = filterTypeOptions[CHILD_FILTER_TYPE].ransackParams(filters, filterKey, childFilterValue);
        Object.assign(paramsForFilter, childRansack);
      }

      return Object.assign(acc, paramsForFilter);
    }, startingObject);
  }

  function debouncedFetchRecords(updatedValues = {}) {
    clearTimeout(fetchRecordsId.current);
    setLoading(true);

    fetchRecordsId.current = setTimeout(() => fetchRecords(updatedValues), 250);
  }

  async function fetchAllMatchingProperties() {
    setLoading(true);
    setEditColumnsModalOpen(false);
    const params = { paginate: false, ...(isCompReport ? { ids_only: true } : {}), ...ransackParams() };

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await get(url, { params, signal: abortControllerRef.current.signal });
      if (isCompReport) return response.compIds;

      return transformRecords(response).map(record => record.uniqueId || record.id);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
        showErrorMessage(`Error fetching records: ${error.message}`);
      }

      return null;
    }
  }

  async function fetchRecords(updatedValues = {}) {
    setLoading(true);
    const filtersSent = updatedValues.selectedFilterValues || report.filters;
    // Only a sort or page change that sends the filters the counts on screen were taken with can keep them.
    const keepCounts = !!updatedValues.keepCounts && isEqual(countedFilters, filtersSent);
    const params = {
      page: updatedValues.currentPage || meta.currentPage,
      limit: pageSize,
      ...ransackParams(updatedValues),
      ...(keepCounts && { counts: false })
    };

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await get(url, { params, signal: abortControllerRef.current.signal });
      const transformedRecords = transformRecords(response);
      setRecords(transformedRecords);
      // A response without counts leaves them out, so the ones the page holds stay.
      setMeta(previous => (keepCounts ? { ...previous, ...response.meta } : response.meta));
      setCountedFilters(filtersSent);
      if (allSelected) {
        setSelectedRecordIds(transformedRecords.map(record => record.uniqueId || record.id));
      }
      setLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
        showErrorMessage(`Error fetching records: ${error.message}`);
      }
    }
  }

  const handleSelectRecord = (record) => {
    const recordId = record.uniqueId || record.id;

    if (isSelected(record)) {
      const newList = selectedRecordIds.filter(id => id !== recordId);
      setSelectedRecordIds(newList);
    } else {
      setSelectedRecordIds(selectedRecordIds => [...selectedRecordIds, recordId]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedRecordIds([]);
      setAllSelected(false);
    } else {
      setSelectedRecordIds(records.map(record => record.uniqueId || record.id));
      setAllSelected(true);
    }
  };

  const handleSortUpdate = (column) => {
    let newSortOrder = 'asc';
    if (report.sorts.column === column) newSortOrder = report.sorts.direction === 'asc' ? 'desc' : 'asc';

    setReport({
      ...report,
      sorts: {
        column: column,
        direction: newSortOrder
      }
    });
    setMeta({ ...meta, currentPage: 1 });
    debouncedFetchRecords({
      currentPage: 1, sortColumn: column, sortOrder: newSortOrder, keepCounts: countOnlyOnFilterChange
    });
  };

  function handlePageChange(e) {
    setMeta({ ...meta, currentPage: e });
    debouncedFetchRecords({ currentPage: e, keepCounts: countOnlyOnFilterChange });
  }

  function updateFilters(updatedFilters) {
    const prunedFilters = deriveFilterValues
      ? pruneStaleSelections(updatedFilters, deriveFilterValues(filterValues, updatedFilters))
      : updatedFilters;

    setReport({
      ...report,
      filters: prunedFilters
    });
    debouncedFetchRecords({ currentPage: 1, selectedFilterValues: prunedFilters });
  }

  function handleFilterChange(event, filterKey, nameOfFilter) {
    const filterObject = filters[nameOfFilter];
    const filterType = filterObject.type;

    let childObject;

    if (filterType === 'accordion' && filterObject.childLabel) {
      childObject = {
        label: filterObject.childLabel,
        values: filterValues[filterObject.childLabel].options.flat()
          .filter(option => option.parentValue === event.currentTarget.value)
      };
    }

    const updatedFilters = filterTypeOptions[filterType]
      .handleFilterChange(event, report.filters, nameOfFilter, filterKey, childObject);
    updateFilters(updatedFilters);
  }

  function removeFilter(nameOfFilter, filterKey, isChild = false) {
    const filterData = filters[nameOfFilter];
    let filterType = filterData.type;
    if (isChild) filterType = CHILD_FILTER_TYPE;

    let childLabel;
    if (filterType === 'accordion' && filterValues[nameOfFilter].childLabel) {
      childLabel = filterValues[nameOfFilter].childLabel;
    }

    const dataToPass = { allFilters: report.filters, nameOfFilter, filterKey, childLabel, filterData };
    const updatedFilters = filterTypeOptions[filterType].removeFilter(dataToPass);
    updateFilters(updatedFilters);
  }

  function clearFilters() {
    const updatedFilters = setupSelectedFilters(filters, false);
    updateFilters(updatedFilters);
  }

  // Back to how the report opens, defaults included, where clearFilters empties every filter.
  function resetFilters() {
    updateFilters(setupSelectedFilters(filters));
  }

  function updatedFilterObject() {
    const effectiveFilterValues = deriveFilterValues
      ? deriveFilterValues(filterValues, report.filters)
      : filterValues;

    return Object.keys(filters).reduce((acc, key) => {
      acc[key] = {
        ...filters[key],
        ...effectiveFilterValues[key],
        handleChange: (event, abc) => handleFilterChange(event, abc, key),
        filterKey: key,
      };

      return acc;
    }, {});
  }

  function searchChanged(event) {
    const updatedText = event.currentTarget.value;
    const updatedReportFilters = {
      ...report.filters,
      search: updatedText
    };
    setReport({
      ...report,
      filters: updatedReportFilters
    });
    debouncedFetchRecords({ currentPage: 1, selectedFilterValues: updatedReportFilters });
  }

  function pageData() {
    if (loading) return {};

    return {
      currentPage: meta.currentPage,
      itemCount: Math.min(meta.totalCount, pageSize),
      itemSingularLabel: itemSingularLabel || humanizeString(reportType),
      pageSize,
      propertySelectedCount: allSelected ? meta.totalCount : selectedRecordIds.length,
      totalCount: meta.totalCount
    };
  }

  // Only once a response has brought the report's counts, and not while the next one loads.
  function settledMeta() {
    return loading || meta.totalCount === undefined ? undefined : meta;
  }

  function reportPageInfo() {
    const settled = settledMeta();

    return settled ? renderPageInfo(settled) : null;
  }

  function changePagePath(newPagePath) {
    setPagePath(newPagePath);
    if (newPagePath === PAGE_PATHS.GENERATE_PDF) {
      setEditColumnsModalOpen(true);
    } else if (newPagePath === PAGE_PATHS.GENERATE_CSV) {
      onGenerateCSV();
    }
  }

  // One filter at a time against its cleared value, since a search and a saved report can add keys the report
  // doesn't configure.
  function filtersApplied() {
    const cleared = setupSelectedFilters(filters, false);

    return Object.keys(filters).some(key => !isEqual(report.filters[key], cleared[key]));
  }

  function renderTable() {
    return (
      <ReportTable
        allSelected={allSelected}
        columns={selectedColumns}
        isSelected={isSelected}
        records={records}
        reportType={reportType}
        selectedRecordIds={selectedRecordIds}
        sortColumn={report.sorts.column}
        sortOrder={report.sorts.direction}
        onRecordSelected={handleSelectRecord}
        onSelectAll={handleSelectAll}
        onSortUpdate={handleSortUpdate}
      />
    );
  }

  function renderResults() {
    // Both empty states offer the same reset.
    const resetFiltersButton = (
      <Button icon={faUndo} variant="ghost" onClick={clearFilters}>
        Reset Filters
      </Button>
    );

    if (records.length > 0) {
      return (
        <React.Fragment>
          <div className="table-container border rounded overflow-auto">
            {renderTable()}
          </div>
          <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} onChange={handlePageChange}/>
        </React.Fragment>
      );
    }

    // A report with its own empty message keeps its header row, with the message under it but outside the sideways
    // scroll, so a wide table can't carry it out of view.
    if (emptyResultsMessage) {
      const filtered = filtersApplied();

      return (
        <div className="border rounded">
          <div className="table-container overflow-auto">
            {renderTable()}
          </div>
          <Empty className="border-0 bg-white">
            <Empty.Title>{filtered ? emptyResultsMessage.title : 'No rows to show'}</Empty.Title>
            {filtered && <Empty.Content>{emptyResultsMessage.content}</Empty.Content>}
            {filtered && <Empty.Actions>{resetFiltersButton}</Empty.Actions>}
          </Empty>
        </div>
      );
    }

    return (
      <Empty className="bg-white" style={{ minHeight: '50vh' }}>
        <Empty.Media>
          <FontAwesomeIcon icon={faSearch} />
        </Empty.Media>
        <Empty.Title>No results found</Empty.Title>
        <Empty.Content>Search with different criteria or reset filters</Empty.Content>
        <Empty.Actions>{resetFiltersButton}</Empty.Actions>
      </Empty>
    );
  }

  function onGenerateCSV() {
    setGeneratingCSVStep(GENERATING_CSV_STEPS.GENERATING);
    setPagePath(PAGE_PATHS.DEFAULT);
    clearInterval(downloadIntervalId.current);
    downloadIntervalId.current = setInterval(checkDownloadComplete, 250);
  }

  function checkDownloadComplete() {
    if (getCookieValue('download_key') === downloadKey.current) {
      clearInterval(downloadIntervalId.current);
      setGeneratingCSVStep(GENERATING_CSV_STEPS.GENERATED);
      setTimeout(() => setGeneratingCSVStep(GENERATING_CSV_STEPS.DEFAULT), 1500);

      const error = getCookieValue(`${downloadKey.current}_error`);
      if (error) alert(error.replace(new RegExp('\\+', 'g'), ' '));
    }
  }

  const columnParams = () => {
    return selectedColumns.map(column => column.name);
  };

  // Captures the resolved CSV export request so a scheduled email job can regenerate
  // this report headlessly (the filter -> ransack translation only exists here in JS).
  // `kind` selects the matching server-side CSV backend in Reports::BuildReportCsv.
  const buildExportQuery = () => {
    const q = {};
    Object.entries(ransackParams()).forEach(([key, value]) => {
      const match = key.match(/^q\[(.+)\]$/);
      if (match) q[match[1]] = value;
    });

    const kindByReportType = {
      property: 'inventory',
      leaseComp: 'lease_comp',
      saleComp: 'sale_comp',
      user: 'user',
      listing: 'listings'
    };

    return {
      kind: kindByReportType[reportType],
      reportClass,
      columns: selectedColumns.map(column => column.label),
      timeZone,
      q
    };
  };

  async function updateReport() {
    // The account only ever sees the defaults, so the view keeps the columns it was saved with.
    const keepSavedColumns = hideColumnCustomization && savedReport?.selectedColumns?.length > 0;
    // Sharing and the name are changed from My reports. The page still holds those of the view it
    // opened on, which after Save As isn't the view being saved.
    const { shared, name, ...reportFields } = report;
    const params = {
      ...reportFields,
      selectedColumns: keepSavedColumns ? savedReport.selectedColumns : columnParams(),
      exportQuery: buildExportQuery()
    };

    try {
      const response = await put(updatedSavedReportUrl, params);
      onSetSavedReport(response);
      updateUIAfterSave(response);
    } catch (error) {
      showFailedUpdateUI(error);
    } finally {
      setShowModal(false);
    }
  }

  async function saveReportAs(inventoryName) {
    const params = {
      ...report,
      name: inventoryName,
      // A copy starts private whatever the open view's sharing; its creator can share it from My reports.
      shared: false,
      selectedColumns: columnParams(),
      exportQuery: buildExportQuery()
    };

    let response;
    try {
      response = await post(savedReportsUrl, params);
      onSetSavedReport(response);
      updateUIAfterSave(response, saveAsMessage);
    } catch (error) {
      showFailedUpdateUI();
    } finally {
      setShowModal(false);
    }
  }

  const updateUIAfterSave = (response, message = 'Report saved successfully!') => {
    showSnackbar({ text: message, variant: 'success' });

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('saved_report_id', response.id);
    window.history.pushState({}, '', currentUrl);
  };

  const showFailedUpdateUI = () => {
    showSnackbar({ text: 'Report did not save successfully', variant: 'danger' });
  };

  const showErrorMessage = (message) => {
    showSnackbar({ text: message, variant: 'danger' });
  };

  const closeColumnSelectModal = () => {
    setPagePath(PAGE_PATHS.DEFAULT);
    setEditColumnsModalOpen(false);
  };

  return (
    <React.Fragment>
      <ReportBreadCrumbs baseReportName={baseReportName} reportsPath={reportsPath} savedReport={savedReport} />

      <div className="container-fluid d-flex flex-column w-100 min-h-0">
        { reportType !== 'user' && !renderFilters && (
          <AllFiltersModal
            allFilterOrder={allFilterOrder}
            filterMapping={updatedFilterObject()}
            show={showAllFiltersModal}
            startingFilters={report.filters}
            onChangeFilters={(updatedFilters) => {
              debouncedFetchRecords({ currentPage: 1, selectedFilterValues: updatedFilters });
              setReport({
                ...report,
                filters: updatedFilters
              });
            }}
            onClearFilters={clearFilters}
            onCloseModal={() => setShowAllFiltersModal(false)}
          />
        )}
        <SavedReportModal
          closeModal={() => {
            setShowModal(false);
          }}
          saveReportName={saveReportAs}
          selectedFilterValues={report.filters}
          show={(showSaveModal)}
        />
        {renderColumnSelect && pagePath === PAGE_PATHS.DEFAULT ? renderColumnSelect({
          defaultColumns: defaultColumns(columns),
          open: editColumnsModalOpen,
          selectedColumns,
          onClose: closeColumnSelectModal,
          onSave: saveSelectedColumns
        }) : (
          <Modal className="multiselect-modal" show={editColumnsModalOpen} size="lg" onHide={closeColumnSelectModal}>
            <ColumnselectModal
              defaultGroupingOptions={defaultGroupingOptions}
              generatePdfOptions={generatePdfOptions}
              generateType={pagePath}
              initiallySelected={selectedColumns}
              key={editColumnsModalOpen}
              optionLabelName="name"
              options={columns}
              optionsLabel="Columns"
              optionValueName="name"
              reorderable={true}
              saveButtonLabel={generatePdfButtonText()}
              saving={generatingPdf}
              setGeneratedPdfOptions={setGeneratePdfOptions}
              title="Edit Columns"
              onClose={closeColumnSelectModal}
              onSave={pagePath === PAGE_PATHS.GENERATE_PDF ? generatePdf : saveSelectedColumns}
            />
          </Modal>
        )}
        <div className="d-flex align-items-center justify-content-between my-3">
          <div>
            <h5 className="m-0">{savedReport?.name || baseReportName}</h5>
            {timeZone && <div className="text-muted small">Timezone: {timeZone}</div>}
          </div>
          <ReportHeaderActions
            allSelected={allSelected}
            csvDownloadMessage={csvDownloadMessage}
            csvDownloadPath={csvDownloadPath}
            csvIgnoresSelection={csvIgnoresSelection}
            disablePdf={disablePdf}
            downloadKey={downloadKey}
            generatingCSVStep={generatingCSVStep}
            hideColumnCustomization={hideColumnCustomization}
            hideSavedReports={hideSavedReports}
            ransackParams={ransackParams}
            savedReport={savedReport}
            selectedColumns={selectedColumns}
            selectedRecordIds={selectedRecordIds}
            timeZone={timeZone}
            totalCount={isEqual(countedFilters, report.filters) ? meta.totalCount : undefined}
            onEditColumns={() => setEditColumnsModalOpen(true)}
            onPathChanged={changePagePath}
            onSaveAs={() => setShowModal(true)}
            onUpdateReport={updateReport}
          />
        </div>
        {renderFilters ? (
          renderFilters({
            filters: report.filters,
            meta: settledMeta(),
            pageInfo: renderPageInfo ? reportPageInfo() : undefined,
            selectedCount: pageData().propertySelectedCount,
            onChangeFilters: updateFilters,
            onResetFilters: resetFilters
          })
        ) : (
          <ReportFilters
            behaviorToggle={behaviorToggle()}
            filterMapping={updatedFilterObject()}
            filters={report.filters}
            pageData={pageData()}
            pageInfo={renderPageInfo ? reportPageInfo() : undefined}
            reportType={reportType}
            searchText={report.filters.search || ''}
            showSearch={Boolean(searchTextRansackLabel)}
            onClearFilters={clearFilters}
            onRemoveFilter={removeFilter}
            onSearchChange={searchChanged}
            onToggleAllFiltersModal={() => setShowAllFiltersModal(true)}
          />
        )}
        {loading ? <LoadingSpinner loadingText={generatingPdf ? loadingPdfText : null}/> : renderResults()}
      </div>
    </React.Fragment>
  );
}

BaseReport.propTypes = {
  // Unused by a report that draws its own filters through renderFilters.
  allFilterOrder: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.object,
        accordionIcons: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            icon: PropTypes.object.isRequired,
          })
        ),
        minPlaceholder: PropTypes.string,
        maxPlaceholder: PropTypes.string,
      })
    )
  ),
  baseReportName: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    // 'end' right-aligns the column, header included.
    align: PropTypes.oneOf(['end']),
    // Draws the column's cells, given the column and the record. Without it a cell shows its value as text.
    component: PropTypes.elementType,
    default: PropTypes.bool.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    locked: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    sortable: PropTypes.bool,
    ransackLabel: PropTypes.string,
  })).isRequired,
  // A sort or page change asks the server for no counts and keeps the ones the page holds. The report's endpoint has
  // to honor counts=false.
  countOnlyOnFilterChange: PropTypes.bool,
  // Given { columnCount, rowCount }, the message shown when a CSV download starts. Omit for none.
  csvDownloadMessage: PropTypes.func,
  // Omit to hide the CSV button.
  csvDownloadPath: PropTypes.string,
  // The CSV button downloads every row the filters return even while rows are ticked.
  csvIgnoresSelection: PropTypes.bool,
  defaultGroupingOptions: PropTypes.object.isRequired,
  deriveFilterValues: PropTypes.func,
  disablePdf: PropTypes.bool,
  // With no rows, shown under the header while a filter is applied. Omit for the page's own empty state.
  emptyResultsMessage: PropTypes.shape({
    content: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }),
  filters: PropTypes.object.isRequired,
  filterValues: PropTypes.object.isRequired,
  hideColumnCustomization: PropTypes.bool,
  hideSavedReports: PropTypes.bool,
  itemSingularLabel: PropTypes.string,
  // Draws the report's own Edit Columns in place of the shared one, given whether it's open, the table's columns, the
  // default columns, and the save and close the shared one uses. The PDF path keeps the shared one.
  renderColumnSelect: PropTypes.func,
  // Draws the report's own filter area in place of the shared bar and All Filters panel. Given the filter values,
  // the latest response's meta and the count line drawn from it (neither while a response loads), the selected row
  // count, a setter that applies new values and a reset to the opening values.
  renderFilters: PropTypes.func,
  // Given the response's meta, draws the report's own line in place of the shared page info. Omit to keep that.
  renderPageInfo: PropTypes.func,
  reportClass: PropTypes.string.isRequired,
  reportPath: PropTypes.string,
  reportsPath: PropTypes.string.isRequired,
  reportType: PropTypes.oneOf(['property', 'leaseComp', 'saleComp', 'user', 'listing']),
  // The message after Save As. Omit for the shared one, which Save always shows.
  saveAsMessage: PropTypes.string,
  savedReport: PropTypes.object,
  savedReportsUrl: PropTypes.string.isRequired,
  // Omit to hide the search field and send no search param.
  searchTextRansackLabel: PropTypes.string,
  timeZone: PropTypes.string,
  transformRecords: PropTypes.func.isRequired,
  updatedSavedReportUrl: PropTypes.string,
  url: PropTypes.string.isRequired,
  onSetSavedReport: PropTypes.func.isRequired,
};
